import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [state, setState] = useState("confirming");
  const [amount, setAmount] = useState(null);
  const [accessCode, setAccessCode] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("aureon_access_amount");
    if (stored) {
      setAmount(Number(stored).toLocaleString());
      sessionStorage.removeItem("aureon_access_amount");
    }
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) {
      setState("error");
      return;
    }
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (data.access_code) setAccessCode(data.access_code);
          clearInterval(t);
        } else if (tries > 15) {
          setState("timeout");
          clearInterval(t);
        }
      } catch {
        if (tries > 15) {
          setState("timeout");
          clearInterval(t);
        }
      }
    }, 2000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center">
      <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">
        Aureon Global Markets · Terminal Access
      </p>
      {state === "confirming" && (
        <div data-testid="payment-confirming">
          <Loader2 className="mx-auto mb-6 h-8 w-8 animate-spin text-gold" />
          <h1 className="font-serif text-4xl font-light tracking-tight text-white sm:text-5xl">
            Confirming your payment…
          </h1>
          <p className="mt-4 text-sm text-white/50">This takes a few seconds.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <h1 className="font-serif text-5xl font-light tracking-tighter text-white sm:text-6xl">
            Access <span className="italic text-gold">granted.</span>
          </h1>
          {amount && (
            <div data-testid="balance-receipt" className="mx-auto mt-10 w-fit border border-gold/30 bg-gold/[0.05] px-10 py-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                Credited to your trading account
              </p>
              <p className="mt-3 font-serif text-5xl font-light tracking-tighter text-gold">
                ${amount}
              </p>
            </div>
          )}
          {accessCode && (
            <div data-testid="access-code-panel" className="mx-auto mt-4 w-fit border border-white/15 px-10 py-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                Your access code — keep it private
              </p>
              <p className="mt-2 font-tabular text-2xl tracking-[0.2em] text-white">
                {accessCode}
              </p>
              <p className="mt-2 text-[10px] text-white/35">
                Also sent to your email. Required to unlock the terminal.
              </p>
            </div>
          )}
          <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-white/50">
            {amount
              ? "Your payment is confirmed — the exact amount above is now your trading balance. No more, no less. The terminal is open."
              : "Your payment is confirmed, and the same amount is now your trading balance. The terminal is open."}
          </p>
          <a
            data-testid="success-enter-terminal-btn"
            href="/terminal"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-10 py-5 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light"
          >
            Enter the Aureon Terminal
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      )}
      {(state === "timeout" || state === "error") && (
        <div data-testid="payment-pending-note">
          <h1 className="font-serif text-4xl font-light tracking-tight text-white sm:text-5xl">
            Almost there.
          </h1>
          <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-white/50">
            Your payment is still being confirmed. If you completed checkout, it
            will reflect shortly — you can safely head to the terminal.
          </p>
          <a
            data-testid="success-enter-terminal-fallback-btn"
            href="/terminal"
            className="mt-10 inline-flex items-center gap-3 rounded-full border border-gold/40 px-10 py-5 text-xs uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
          >
            Go to the terminal
          </a>
        </div>
      )}
    </div>
  );
}
